import {
  MdAddCircleOutline,
  MdOutlineArrowCircleLeft,
  MdOutlineArrowCircleRight,
  MdOutlineGroupAdd,
  MdOutlineMeetingRoom,
} from "react-icons/md";
import ClassDetailsFormCard from "components/main-view/utils/cards/ClassDetailsFormCard";
import {
  ClassListType,
  ContactListType,
  ParentListType,
} from "utils/interfaces";
import { useContext, useState } from "react";
import { AnimatePresence } from "framer-motion";
import AddParentPanel from "../pop-up-panels/add-parent-pop-up";
import AssignClassPanel from "components/main-view/utils/pop-up-panels/assign-class-pop-up";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";
import UserFormCard from "../cards/UserFormCard";

interface ClassParentDetailsProps {
  selectedClasses: ClassListType[];
  selectedContacts: ContactListType[];
  setStage: React.Dispatch<React.SetStateAction<number>>;
  setSelectedClasses: React.Dispatch<React.SetStateAction<ClassListType[]>>;
  setSelectedContacts: React.Dispatch<React.SetStateAction<ContactListType[]>>;
}

const ClassParentDetails: React.FC<ClassParentDetailsProps> = ({
  selectedClasses,
  selectedContacts,
  setStage,
  setSelectedClasses,
  setSelectedContacts,
}) => {
  // States definition
  const [showEditParent, setShowEditParent] = useState(false);
  const [selectedData, setSelectedData] = useState<ContactListType | null>(
    null,
  );
  const [showAddClassesPopUp, setShowAddClassesPopUp] = useState(false);
  const [showAddParentsPopUp, setShowAddParentsPopUp] = useState(false);

  // Get the values from the context
  const { contextSchoolByRole } = useContext(MainContext);
  const { dataRequirements, classesTerminology } = usePlatform();

  // Define handle previous button
  const handlePrev = () => {
    setStage(1);
  };

  // Define handle next button
  const handleNext = () => {
    setStage(3);
  };

  // Definition of add class action
  const handleAddClass = () => {
    setShowAddClassesPopUp(true);
  };

  // Definition of add parent action
  const handleAddParent = () => {
    setShowAddParentsPopUp(true);
  };

  /**
   * Function used to operate on click of the delete button
   */
  const handleDeleteParent = (contact: ContactListType) => {
    // Deleting the contact with the current id
    const contactsFiltered = selectedContacts.filter(
      (currentContact: ContactListType) => currentContact._id !== contact._id,
    );
    if (setSelectedContacts) setSelectedContacts(contactsFiltered);
  };

  /**
   * Function used to operate on click of the edit/open button
   */
  const handleEditParent = (contact: ContactListType) => {
    // Select the current data
    setSelectedData(contact);
    // And display the pop up
    setShowEditParent(true);
  };

  return (
    <div className="w-[562px] h-full p-6 bg-white rounded-3xl border-neutral-200 justify-start items-start gap-12 flex-col inline-flex">
      <div className="self-stretch h-7 flex-col justify-start items-start flex text-neutral-700 text-[23px] font-semibold font-sans leading-7">
        {/* {singlify(classesTerminology)} and Parent details */}
        {singlify(classesTerminology)} details
      </div>
      {/* {dataRequirements.includes("className") && ( */}
      <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
        <div className="self-stretch h-[19px] flex-col justify-start items-start flex text-neutral-500 text-base font-semibold font-sans leading-[19px]">
          {singlify(classesTerminology)} details
        </div>
        {/* Show the add class button, or the list of possible classes */}
        {selectedClasses.length === 0 ? (
          <button
            className="w-full px-2 py-3 rounded-lg border border-dashed border-neutral-300 hover:bg-neutral-25 ease-in-out duration-150 justify-center items-center gap-2 inline-flex cursor-pointer"
            onClick={handleAddClass}
          >
            <MdOutlineMeetingRoom className="w-6 h-6 fill-neutral-500" />
            <div className="text-neutral-500 text-base font-normal font-sans leading-[19px]">
              Add a {singlify(classesTerminology).toLowerCase()}
            </div>
          </button>
        ) : (
          <>
            {selectedClasses.map((classElem: ClassListType, index: number) => (
              <ClassDetailsFormCard
                key={index}
                thisClass={classElem}
                setSelectedClasses={setSelectedClasses}
              />
            ))}
            <div className="w-[514px] h-6 justify-between items-center inline-flex">
              <button
                className="group rounded justify-center items-center gap-2 flex cursor-pointer"
                onClick={handleAddClass}
              >
                <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                  Add additional {singlify(classesTerminology).toLowerCase()}
                </div>
              </button>
            </div>
          </>
        )}
      </div>
      {/* Remove parent details from the UI at the moment */}
      {false && dataRequirements.includes("parentDetails") && (
        <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
          <div className="self-stretch h-[19px] flex-col justify-start items-start flex text-neutral-500 text-base font-semibold font-sans leading-[19px]">
            Parent and Guardian details
          </div>
          {selectedContacts.length === 0 ? (
            <button
              className="w-full px-2 py-3 rounded-lg border border-dashed border-neutral-300 hover:bg-neutral-25 ease-in-out duration-150 justify-center items-center gap-2 inline-flex cursor-pointer"
              onClick={handleAddParent}
            >
              <MdOutlineGroupAdd className="w-6 h-6 fill-neutral-500" />
              <div className="text-neutral-500 text-base font-normal font-sans leading-[19px]">
                Add a parent/guardian
              </div>
            </button>
          ) : (
            <>
              {selectedContacts.map(
                (contact: ContactListType, index: number) => (
                  <UserFormCard
                    key={index}
                    user={contact}
                    selectedUsers={selectedContacts}
                    primaryTag={true}
                    handleDelete={() => handleDeleteParent(contact)}
                    handleEdit={() => handleEditParent(contact)}
                  />
                ),
              )}
              <div className="w-[514px] h-6 justify-between items-center inline-flex">
                <button
                  className=" group rounded justify-center items-center gap-2 flex cursor-pointer"
                  onClick={handleAddParent}
                >
                  <MdAddCircleOutline className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
                  <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                    Add additional Parent/Guardian
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <div className="h-[46px] flex flex-row justify-between items-center w-full">
        <button
          className="group w-fit h-fit flex rounded justify-center items-center gap-2 cursor-pointer"
          onClick={handlePrev}
        >
          <MdOutlineArrowCircleLeft className="w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700" />
          <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans">
            Prev
          </div>
        </button>
        <button
          className="group w-fit h-fit flex rounded justify-center items-center gap-2 cursor-pointer"
          onClick={handleNext}
        >
          <div
            className={
              true
                ? "text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans"
                : "text-neutral-200 text-lg font-semibold font-sans"
            }
          >
            Next
          </div>
          <MdOutlineArrowCircleRight
            className={
              true
                ? "w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700"
                : "w-6 h-6 fill-neutral-200"
            }
          />
        </button>
      </div>
      <AnimatePresence>
        {/* Assign to class pop-up */}
        {showAddClassesPopUp && (
          <AssignClassPanel
            key="assign-class-panel"
            selectedClasses={selectedClasses}
            setShowPopUp={setShowAddClassesPopUp}
            setSelectedClasses={setSelectedClasses}
          />
        )}
        {/* Edit parent pop-up */}
        {showEditParent && (
          <AddParentPanel
            key="edit-parent-panel"
            setShowPopUp={setShowEditParent}
            selectedData={selectedData as ParentListType}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
          />
        )}
        {/* Add parent pop-up */}
        {showAddParentsPopUp && (
          <AddParentPanel
            key="add-parent-panel"
            contextSchool={contextSchoolByRole}
            setShowPopUp={setShowAddParentsPopUp}
            selectedContacts={selectedContacts}
            setSelectedContacts={setSelectedContacts}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClassParentDetails;
